/**
 * Genie avatar background colours (HG-4555).
 *
 * The avatar circle is filled with the brand primary colour mixed toward
 * white, plus a soft white highlight, and (on the voice surfaces) sits on a
 * translucent brand-tinted halo. `GenieBranding.avatarBackgroundOpacity`
 * (0–100) lets users pick how strong that primary colour is — 100 renders
 * the circle as a full solid colour. When unset, the legacy look (28% tint)
 * is returned unchanged.
 */

/** Brand weight used by the legacy (unset) avatar background. */
export const DEFAULT_AVATAR_BACKGROUND_OPACITY = 28;

const LEGACY_HIGHLIGHT = "radial-gradient(circle at 50% 40%, rgba(255,255,255,0.26) 0%, rgba(255,255,255,0) 58%)";
/** Halo alpha per unit of brand weight — 0.28 weight → ~0x20 alpha, matching the legacy halo. */
const HALO_ALPHA_PER_WEIGHT = 0.125 / 0.28;

function parseHex(hex: string): [number, number, number] {
  const match = hex.replace("#", "");
  return [
    parseInt(match.substring(0, 2), 16) || 0,
    parseInt(match.substring(2, 4), 16) || 0,
    parseInt(match.substring(4, 6), 16) || 0,
  ];
}

export function mixWithWhite(hex: string, brandWeight: number): string {
  const [r, g, b] = parseHex(hex);
  const weight = Math.max(0, Math.min(1, brandWeight));
  const toHex = (v: number) => Math.round(255 * (1 - weight) + v * weight).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function hexToRgba(hex: string, alpha: number): string {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${Number(alpha.toFixed(3))})`;
}

/** Clamps a stored 0–100 opacity; returns null when unset/invalid (legacy look). */
export function normalizeAvatarBackgroundOpacity(value: number | null | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.max(0, Math.min(100, Math.round(value)));
}

interface AvatarBackgroundInput {
  color: string;
  secondaryColor?: string | null;
  gradientEnabled?: boolean;
  gradientAngle?: number;
  opacity?: number | null;
}

/** CSS `background` for the avatar circle itself. */
export function getAvatarCircleBackground({
  color,
  secondaryColor,
  gradientEnabled,
  gradientAngle,
  opacity,
}: AvatarBackgroundInput): string {
  const normalized = normalizeAvatarBackgroundOpacity(opacity);
  const weight = (normalized ?? DEFAULT_AVATAR_BACKGROUND_OPACITY) / 100;
  const primary = mixWithWhite(color, weight);
  const baseFill =
    gradientEnabled && secondaryColor
      ? `linear-gradient(${gradientAngle || 135}deg, ${primary}, ${mixWithWhite(secondaryColor, weight)})`
      : primary;
  // Full solid colour drops the white highlight so the fill reads as one flat colour.
  return normalized === 100 ? baseFill : `${LEGACY_HIGHLIGHT}, ${baseFill}`;
}

/** CSS `background` for the translucent halo wrapped around voice-surface avatars. */
export function getAvatarHaloBackground(color: string, opacity?: number | null): string {
  const normalized = normalizeAvatarBackgroundOpacity(opacity);
  if (normalized === null) return `${color}20`;
  return hexToRgba(color, Math.min(1, (normalized / 100) * HALO_ALPHA_PER_WEIGHT));
}
